/*
 * Copyright 2023 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ensureStringEnumSupportedOrUndefined } from "./wireObjectTypeFullMetadataToSdkObjectMetadata.js";
import { wirePropertyFormattingToSdkFormatting } from "./wirePropertyFormattingToSdkFormatting.js";
const supportedMarkingTypes = ["CBAC", "MANDATORY"];

/**
 * Extracts main value metadata from a struct property type.
 * Returns the fields array that comprise the main value.
 */
function extractMainValue(dataType) {
  if (dataType.type === "struct" && dataType.mainValue) {
    const fields = dataType.mainValue.fields;
    if (fields && fields.length > 0) {
      return {
        fields
      };
    }
  }
  return undefined;
}

/**
 * Checks if an array property type has reducers.
 */
function hasReducers(dataType) {
  return dataType.type === "array" && dataType.reducers != null && dataType.reducers.length > 0;
}

/**
 * Builds the per-`type` {@link ObjectMetadata.PropertyTypeMetadata} for a
 * wire property dataType, looking through `array` to its `subType`. Returns
 * an object that can be spread directly into the property — either
 * `{ typeMetadata: ... }` when a variant applies, or `{}` otherwise — so
 * non-applicable properties don't get a `typeMetadata: undefined` own key.
 * New `typeMetadata` variants should be added here so each call site stays
 * a one-liner.
 */
function extractTypeMetadata(dataType) {
  const inner = dataType.type === "array" ? dataType.subType : dataType;
  if (inner.type === "marking") {
    const markingType = ensureStringEnumSupportedOrUndefined(inner.markingType, supportedMarkingTypes);
    return {
      typeMetadata: {
        type: "marking",
        ...(markingType != null ? {
          markingType
        } : {})
      }
    };
  }
  return {};
}
export function wirePropertyV2ToSdkPropertyDefinition(input, isNullable = true, log) {
  const sdkPropDefinition = objectPropertyTypeToSdkPropertyDefinition(input.dataType, log);
  if (sdkPropDefinition == null) {
    return undefined;
  }
  switch (input.dataType.type) {
    case "integer":
    case "string":
    case "byte":
    case "decimal":
    case "double":
    case "float":
    case "long":
    case "short":
    case "boolean":
    case "date":
    case "attachment":
    case "mediaReference":
    case "geopoint":
    case "geoshape":
    case "timestamp":
    case "timeseries":
    case "geotimeSeriesReference":
    case "vector":
    case "cipherText":
      return {
        displayName: input.displayName,
        multiplicity: false,
        description: input.description,
        type: sdkPropDefinition,
        nullable: input.nullable == null ? isNullable : input.nullable,
        valueTypeApiName: input.valueTypeApiName,
        valueFormatting: input.valueFormatting != null ? wirePropertyFormattingToSdkFormatting(input.valueFormatting, log) : undefined
      };
    case "marking":
      return {
        displayName: input.displayName,
        multiplicity: false,
        description: input.description,
        type: sdkPropDefinition,
        nullable: input.nullable == null ? isNullable : input.nullable,
        valueTypeApiName: input.valueTypeApiName,
        valueFormatting: input.valueFormatting != null ? wirePropertyFormattingToSdkFormatting(input.valueFormatting, log) : undefined,
        ...extractTypeMetadata(input.dataType)
      };
    case "struct":
      {
        const mainValue = extractMainValue(input.dataType);
        return {
          displayName: input.displayName,
          multiplicity: false,
          description: input.description,
          type: sdkPropDefinition,
          nullable: input.nullable == null ? isNullable : input.nullable,
          valueTypeApiName: input.valueTypeApiName,
          valueFormatting: input.valueFormatting != null ? wirePropertyFormattingToSdkFormatting(input.valueFormatting, log) : undefined,
          mainValue
        };
      }
    case "array":
      {
        return {
          displayName: input.displayName,
          multiplicity: true,
          description: input.description,
          type: sdkPropDefinition,
          nullable: true,
          valueTypeApiName: input.valueTypeApiName,
          valueFormatting: input.valueFormatting != null ? wirePropertyFormattingToSdkFormatting(input.valueFormatting, log) : undefined,
          hasReducers: hasReducers(input.dataType),
          ...extractTypeMetadata(input.dataType)
        };
      }
    default:
      input.dataType;
      log?.info(`${JSON.stringify(input.dataType)} is not a supported dataType`);
      return undefined;
  }
}
function objectPropertyTypeToSdkPropertyDefinition(propertyType, log) {
  switch (propertyType.type) {
    case "integer":
    case "string":
    case "byte":
    case "decimal":
    case "double":
    case "float":
    case "long":
    case "short":
    case "boolean":
    case "attachment":
    case "geopoint":
    case "geoshape":
    case "timestamp":
    case "marking":
    case "geotimeSeriesReference":
    case "mediaReference":
    case "vector":
    case "cipherText":
      return propertyType.type;
    case "date":
      return "datetime";
    case "array":
      return objectPropertyTypeToSdkPropertyDefinition(propertyType.subType);
    case "timeseries":
      if (propertyType.itemType?.type === "string") {
        return "stringTimeseries";
      } else if (propertyType.itemType?.type === "double") {
        return "numericTimeseries";
      } else return "sensorTimeseries";
    case "struct":
      {
        return propertyType.structFieldTypes.reduce((structMap, structField) => {
          structMap[structField.apiName] = objectPropertyTypeToSdkPropertyDefinition(structField.dataType);
          return structMap;
        }, {});
      }
    default:
      {
        log?.info(`${JSON.stringify(propertyType)} is not a supported propertyType`);
        return undefined;
      }
  }
}